import { motion } from "framer-motion";
import { ArrowUpRight, Droplets, Home, Paintbrush, RefreshCcw, Sparkles, Wrench } from "lucide-react";

const SERVICES = [
  {
    id: "gutter-cleans",
    num: "01",
    title: "Gutter Cleans",
    icon: Droplets,
    desc: "Clear out leaves, debris, and moss to prevent overflow and water damage to your home.",
    points: ["Full debris & moss removal", "Downpipe flush & flow check", "Problem areas flagged"],
    img: "https://images.unsplash.com/photo-1634853982486-c06f0e17940f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxndXR0ZXIlMjBtYWludGVuYW5jZSUyMHJhaW4lMjBtZXRhbCUyMHJvb2YlMjBjb250cmFjdG9yfGVufDB8fHx8MTc4OTY0NDYyNXww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "gutter-repairs",
    num: "02",
    title: "Gutter Repairs",
    icon: Wrench,
    desc: "Fix leaks, sagging gutters, loose brackets, and joint seals to ensure proper drainage.",
    points: ["Leak & joint resealing", "Bracket & fall correction", "Storm damage tidy-ups"],
    img: "https://images.unsplash.com/photo-1600093652298-e57ee8aba769?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxndXR0ZXIlMjBtYWludGVuYW5jZSUyMHJhaW4lMjBtZXRhbCUyMHJvb2YlMjBjb250cmFjdG9yfGVufDB8fHx8MTc4OTY0NDYyNXww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "gutter-replacements",
    num: "03",
    title: "Gutter Replacements",
    icon: RefreshCcw,
    desc: "Full removal and installation of durable, long-lasting modern guttering systems.",
    points: ["Old guttering removed", "Modern profiles & colours", "Correct fall, clean finish"],
    img: "https://images.unsplash.com/photo-1759105734106-47594c3eea11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxndXR0ZXIlMjBtYWludGVuYW5jZSUyMHJhaW4lMjBtZXRhbCUyMHJvb2YlMjBjb250cmFjdG9yfGVufDB8fHx8MTc4OTY0NDYyNXww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "roof-cleans",
    num: "04",
    title: "Roof Cleans",
    icon: Sparkles,
    desc: "Professional treatment and wash to remove lichen, moss, mould, and grime built up over time.",
    points: ["Lichen & moss treatment", "Low-pressure soft wash", "Slows regrowth for years"],
    img: "https://images.pexels.com/photos/12641061/pexels-photo-12641061.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "roof-paints",
    num: "05",
    title: "Roof Paints",
    icon: Paintbrush,
    desc: "High-quality roof restoration coatings and paints to protect against harsh coastal Taranaki weather.",
    points: ["Full restoration coating", "Coastal-rated products", "Instant street appeal"],
    img: "https://images.pexels.com/photos/8853521/pexels-photo-8853521.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    id: "roof-replacements",
    num: "06",
    title: "Roof Replacements",
    icon: Home,
    desc: "Complete re-roofing solutions tailored to protect and add value to your property.",
    points: ["Full strip & re-roof", "Long-run iron specialists", "Adds real property value"],
    img: "https://images.unsplash.com/photo-1773432114061-2ae201208630?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxyb29maW5nJTIwcmVwYWlyJTIwY29uc3RydWN0aW9uJTIwaG91c2UlMjByb29mfGVufDB8fHx8MTc4OTY0NDYyNWww&ixlib=rb-4.1.0&q=85",
  },
];

function quoteService(title) {
  window.dispatchEvent(new CustomEvent("trc:service", { detail: title }));
  const target = document.getElementById("quote");
  if (window.__lenis && target) window.__lenis.scrollTo(target, { offset: -72 });
  else target?.scrollIntoView({ behavior: "smooth" });
}

export default function Services() {
  return (
    <section id="services" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-16 grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-orange-400">01 — What we do</p>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
              Six ways we keep
              <br />
              the weather out.
            </h2>
          </div>
          <p className="max-w-md text-base leading-relaxed text-slate-400 lg:col-span-5 lg:justify-self-end">
            Every job is quoted straight, done properly, and built for the wind, salt and rain that
            come with living under the maunga.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.id}
              data-testid={`service-card-${s.id}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="group flex flex-col border border-white/[0.08] bg-[#131924] transition-[border-color,transform] duration-300 hover:-translate-y-1.5 hover:border-orange-500/40"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.title}
                  loading="lazy"
                  className="h-full w-full object-cover grayscale-[40%] transition-[filter,transform] duration-500 group-hover:scale-[1.04] group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#131924] via-transparent to-transparent" />
                <span className="absolute left-4 top-4 font-mono text-xs font-semibold tracking-[0.3em] text-orange-400">
                  {s.num}
                </span>
              </div>

              <div className="flex flex-1 flex-col p-6">
                <div className="mb-3 flex items-center gap-3">
                  <s.icon className="h-5 w-5 text-orange-400" strokeWidth={2} />
                  <h3 className="font-heading text-lg font-semibold tracking-normal text-slate-100 sm:text-xl">
                    {s.title}
                  </h3>
                </div>
                <p className="text-sm leading-relaxed text-slate-400">{s.desc}</p>
                <ul className="mt-5 space-y-2">
                  {s.points.map((p) => (
                    <li key={p} className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.12em] text-slate-300">
                      <span className="text-orange-500">+</span> {p}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  data-testid={`service-quote-${s.id}`}
                  onClick={() => quoteService(s.title)}
                  className="mt-6 inline-flex items-center gap-2 self-start border-b border-orange-500/40 pb-1 font-mono text-xs uppercase tracking-[0.2em] text-orange-400 transition-colors duration-200 hover:border-orange-400 hover:text-orange-300"
                >
                  Quote this service
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
